import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Sparkles, ShieldCheck, Wallet, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { buildReferralLink } from "@/lib/referral";
import { CreceLogo } from "@/components/CreceLogo";

export default function Flyer() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Crece · Partner Flyer";
    return () => { document.title = "Crece · Financial Career Platform"; };
  }, []);

  if (!user) return <div className="p-8 text-stone-500">Loading...</div>;

  const ref = user.referral_code;
  const link = buildReferralLink(ref, "flyer", "print");
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=0&data=${encodeURIComponent(link)}`;

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white">
      {/* No-print toolbar */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-stone-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-emerald-900">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-sm text-stone-500">Tip: choose <b>"Save as PDF"</b> in the print dialog.</div>
        <Button data-testid="download-flyer-button" onClick={() => window.print()}
          className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
          <Printer className="w-4 h-4 mr-2" /> Download PDF
        </Button>
      </div>

      {/* A4 flyer sheet */}
      <div className="py-8 print:py-0 grid place-items-center">
        <div data-testid="flyer-sheet"
          className="bg-[#FDFBF7] shadow-xl print:shadow-none relative overflow-hidden print:break-inside-avoid"
          style={{ width: "210mm", height: "297mm", padding: "16mm", pageBreakInside: "avoid", breakInside: "avoid" }}>

          {/* Header */}
          <div className="flex items-center justify-between border-b border-stone-300 pb-5">
            <div className="flex items-center gap-2.5">
              <CreceLogo size="md" theme="dark" />
              <span className="font-display font-black text-2xl tracking-tight">Crece</span>
            </div>
            <div className="text-right text-xs text-stone-600">
              <div className="font-bold uppercase tracking-widest text-emerald-800">Authorized Partner</div>
              <div className="font-mono mt-1">+91 {user.mobile}</div>
            </div>
          </div>

          {/* Hero */}
          <div className="mt-10">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> India's #1 Financial Career Platform
            </div>
            <h1 className="font-display text-6xl font-black tracking-tight leading-[0.95] mt-5">
              Be boss of <br /> your own <span className="text-amber-600 italic font-light">earnings</span>.
            </h1>
            <p className="mt-5 text-stone-700 max-w-md text-lg leading-snug">
              Refer credit cards, loans, and insurance. Build a 10-grade career. Earn weekly via NEFT — no investment, no experience.
            </p>
          </div>

          {/* QR + Code */}
          <div className="mt-10 bg-emerald-900 text-stone-100 rounded-3xl p-7 flex items-center gap-7">
            <div className="bg-white p-3 rounded-2xl">
              <img src={qrUrl} alt="QR code to sign up" className="w-40 h-40" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Scan to join</div>
              <div className="text-sm text-stone-300 mt-1">Or use referral code</div>
              <div className="font-display text-5xl font-black tracking-wider mt-2 text-amber-400" data-testid="flyer-ref-code">{ref}</div>
              <div className="mt-4 text-sm text-stone-300">Invited by <b className="text-stone-50">{user.name}</b></div>
            </div>
          </div>

          {/* Benefits */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <Wallet className="w-5 h-5 text-emerald-900" />
              <div className="font-display font-bold text-lg mt-3">Weekly NEFT</div>
              <div className="text-xs text-stone-600 mt-1">Direct to your bank account, every week.</div>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <TrendingUp className="w-5 h-5 text-amber-600" />
              <div className="font-display font-bold text-lg mt-3">10-Grade Career</div>
              <div className="text-xs text-stone-600 mt-1">Earn from 10 grades — your team and theirs.</div>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <ShieldCheck className="w-5 h-5 text-emerald-900" />
              <div className="font-display font-bold text-lg mt-3">100% Free</div>
              <div className="text-xs text-stone-600 mt-1">No joining fee, no investment.</div>
            </div>
          </div>

          {/* Footer */}
          <div className="absolute bottom-[16mm] left-[16mm] right-[16mm] pt-5 border-t border-stone-300 flex items-end justify-between text-xs text-stone-500">
            <div>
              <div className="font-bold text-stone-900">Sign up now</div>
              <div className="font-mono mt-0.5 truncate">{link}</div>
            </div>
            <div className="text-right">
              <div>Free to Join · No Investment · Weekly Payouts</div>
              <div className="mt-0.5">© 2026 Crece</div>
            </div>
          </div>
        </div>
      </div>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; height: auto !important; }
          /* Force browsers to honor background colors & images in print/PDF */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}
